/**
 * usePageTracking Hook
 * Automatically tracks page views on route changes
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView, trackEvent } from './analytics.service';
import { PAGE_VIEW_EVENTS, ADMIN_EVENTS } from './events';

interface UsePageTrackingOptions {
  // Whether to track page views (default: true)
  enabled?: boolean;
}

/**
 * Hook to automatically track page views on route changes
 * Should be used once in the main App component
 */
export const usePageTracking = (options: UsePageTrackingOptions = {}): void => {
  const { enabled = true } = options;
  const location = useLocation();
  const previousPathRef = useRef<string>('');

  useEffect(() => {
    if (!enabled) return;

    const currentPath = location.pathname;

    // Skip if path hasn't changed (prevents double tracking)
    if (currentPath === previousPathRef.current) return;

    previousPathRef.current = currentPath;

    // Get the specific event name for this route, or use generic page_view
    const eventName = getEventNameForPath(currentPath);

    // Track the page view
    if (eventName) {
      // If it's an admin page, include additional parameters
      if (eventName === ADMIN_EVENTS.ADMIN_PAGE_VIEW) {
        trackEvent(eventName, {
          page_name: getAdminPageName(currentPath),
        });
      } else {
        trackPageView(currentPath);
      }
    } else {
      // Generic page view for routes not in our mapping
      trackPageView(currentPath);
    }
  }, [location.pathname, enabled]);
};

/**
 * Get the specific event name for a given path
 */
const getEventNameForPath = (path: string): string | undefined => {
  // Check for exact match first
  const exactMatch = PAGE_VIEW_EVENTS[path as keyof typeof PAGE_VIEW_EVENTS];
  if (exactMatch) return exactMatch;

  // Check for pattern matches (routes with parameters)
  for (const [routePattern, eventName] of Object.entries(PAGE_VIEW_EVENTS)) {
    // Skip exact matches (already handled)
    if (!routePattern.includes(':')) continue;

    // Convert route pattern to regex
    const regexPattern = routePattern.replace(/:[^/]+/g, '[^/]+');
    const regex = new RegExp(`^${regexPattern}$`);

    if (regex.test(path)) {
      return eventName;
    }
  }

  // Check for prefix matches (e.g., /teams/123 matches /teams)
  const pathParts = path.split('/').filter(Boolean);
  for (let i = pathParts.length; i > 0; i--) {
    const partialPath = '/' + pathParts.slice(0, i).join('/');
    const match = PAGE_VIEW_EVENTS[partialPath as keyof typeof PAGE_VIEW_EVENTS];
    if (match) return match;
  }

  return undefined;
};

/**
 * Get a human-readable page name for admin pages
 */
const getAdminPageName = (path: string): string => {
  const pageNames: Record<string, string> = {
    '/customer-details': 'Customer Details',
    '/users-list': 'Users List',
    '/artist-mapping': 'Artist Mapping',
    '/admin/roles': 'Roles Management',
    '/admin/nebula-insights': 'Nebula Insights',
    '/permissions': 'Permissions',
    '/admin/invitations': 'Invitations',
  };

  return pageNames[path] || path;
};

export default usePageTracking;
